import { useState } from "react";
import { Goal, Square, Repeat, StickyNote, Flag, Clock, Video, AlertTriangle } from "lucide-react";
import { label } from "../lib/api";
import { Empty } from "./kit";

const ICON = { goal: Goal, own_goal: Goal, penalty_scored: Goal, substitution: Repeat, note: StickyNote, foul: Flag, period: Clock, full_time: Clock, var_review: Video, suspension: AlertTriangle, resumption: Clock };
const FILTERS = { All: null, Goals: ["goal", "own_goal", "penalty_scored", "penalty_missed"], Cards: ["yellow", "second_yellow", "red"], Subs: ["substitution"] };

const CardIcon = ({ type }) => (
  <span className={`inline-block h-4 w-3 rounded-[2px] ${type === "yellow" ? "bg-[#FFD400]" : type === "second_yellow" ? "bg-gradient-to-br from-[#FFD400] from-50% to-[#F02030] to-50%" : "bg-[#F02030]"}`} />
);

export function EventTimeline({ events, match, onCorrect, filter: initial = "All", testid = "event-timeline", compact }) {
  const [filter, setFilter] = useState(initial);
  const P = (id) => match?.players?.[id];
  const list = [...events].filter((e) => !FILTERS[filter] || FILTERS[filter].includes(e.type)).reverse();
  return (
    <div data-testid={testid}>
      <div className="mb-3 flex gap-1.5">
        {Object.keys(FILTERS).map((f) => (
          <button key={f} data-testid={`timeline-filter-${f.toLowerCase()}`} onClick={() => setFilter(f)}
            className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${filter === f ? "bg-[#17191C] text-white" : "bg-[#F7F8F9] text-[#343A40] hover:bg-[#E6E9ED]"}`}>{f}</button>
        ))}
      </div>
      {!list.length ? <Empty text="No events recorded yet." /> : (
        <ul className="space-y-1.5">
          {list.map((e) => {
            const Icon = ICON[e.type];
            const pl = P(e.player_id), pin = P(e.player_in_id);
            const team = e.team_id === match?.home_team_id ? match?.home?.short : e.team_id ? match?.away?.short : "";
            return (
              <li key={e.id} data-testid={`event-${e.type}-${e.minute}`} className={`flex items-center gap-3 rounded-lg border px-3 py-2 text-sm ${e.status === "voided" ? "border-dashed opacity-50 line-through" : "border-[#E6E9ED]"} ${e.queued ? "bg-[#FFD400]/15" : "bg-white"}`}>
                <span className="w-10 shrink-0 font-mono font-bold">{e.minute + 1}'</span>
                <span className="flex w-5 justify-center">{["yellow", "second_yellow", "red"].includes(e.type) ? <CardIcon type={e.type} /> : Icon ? <Icon className="h-4 w-4 text-[#343A40]" /> : null}</span>
                <span className="w-10 shrink-0 text-xs font-bold text-[#6C757D]">{team}</span>
                <span className={`min-w-0 flex-1 ${compact ? "break-words" : "truncate"}`}>
                  <b>{label(e.type)}</b>
                  {pl && <> · #{pl.number} {pl.short_name}</>}{pin && <> → #{pin.number} {pin.short_name}</>}
                  {e.notes && <span className="text-[#6C757D]"> — {e.notes}</span>}
                </span>
                {!compact && <span className="hidden text-[10px] font-bold uppercase text-[#6C757D] sm:inline">{e.queued ? "queued" : e.recorded_offline ? "synced" : e.corrections?.length ? "corrected" : e.status}</span>}
                {onCorrect && e.status === "active" && !e.queued && !["period", "full_time"].includes(e.type) && (
                  <button data-testid={`correct-event-${e.id}`} onClick={() => onCorrect(e)} className="rounded-md border border-[#C5CBD1] px-2 py-0.5 text-[11px] font-bold hover:bg-[#F7F8F9]">Correct</button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
